import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Eye, EyeOff } from "lucide-react"; // Importing the eye icons

const LoginPage = () => {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false); // State to toggle visibility
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setIsLoading(true);

    try {
      const response = await fetch(
        "https://mosque-backend-yfae.vercel.app/login",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ username, password }),
        },
      );

      const data = await response.json();

      if (data.success) {
        localStorage.setItem("username", data.role);
        navigate("/admin/dashboard");
      } else {
        setError(data.message || "Invalid credentials. Please try again.");
      }
    } catch (err) {
      setError("Something went wrong. Please check your network connection.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div
      className="relative flex items-center justify-center min-h-screen bg-stone-900 bg-cover bg-center px-4"
      style={{ backgroundImage: "url('/your-mosque-bg-if-any.jpg')" }}
    >
      {/* Premium Modern Backdrop Mask */}
      <div className="absolute inset-0 bg-gradient-to-tr from-emerald-950/90 via-stone-900/85 to-emerald-900/40 z-0" />

      {/* Login Card */}
      <div className="relative z-10 w-full max-w-md bg-white/95 backdrop-blur-md p-8 md:p-10 rounded-2xl shadow-2xl border border-white/20 transition-all duration-300">
        {/* Top Header Decorator */}
        <div className="text-center mb-8">
          <h2
            className="text-3xl font-serif text-emerald-800 tracking-wide mb-1"
            dir="rtl"
          >
            مَرْحَبًا بِكُمْ
          </h2>
          <p className="text-xs uppercase tracking-[0.2em] text-amber-600 font-semibold mb-6">
            Portal Gateway
          </p>
          <h3 className="text-xl font-bold text-slate-800 tracking-tight">
            Admin Authentication
          </h3>
        </div>

        {/* Error Feedback Banner */}
        {error && (
          <div className="mb-5 p-3 rounded-xl bg-red-50 border border-red-200 text-red-600 text-sm text-center font-medium">
            {error}
          </div>
        )}

        {/* Input Form */}
        <form onSubmit={handleSubmit} className="space-y-5">
          <div>
            <label className="block text-xs font-bold tracking-wider uppercase text-slate-600 mb-2">
              Username
            </label>
            <input
              type="text"
              className="w-full px-4 py-3 bg-stone-50 border border-slate-200 focus:border-emerald-600 focus:bg-white focus:ring-4 focus:ring-emerald-600/10 rounded-xl outline-none transition-all duration-200 text-slate-800"
              placeholder="Enter your administrative ID"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              required
            />
          </div>

          <div className="relative">
            <label className="block text-xs font-bold tracking-wider uppercase text-slate-600 mb-2">
              Password
            </label>
            <input
              type={showPassword ? "text" : "password"} // Dynamic type switching
              className="w-full px-4 py-3 bg-stone-50 border border-slate-200 focus:border-emerald-600 focus:bg-white focus:ring-4 focus:ring-emerald-600/10 rounded-xl outline-none transition-all duration-200 text-slate-800 pr-12"
              placeholder="••••••••"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
            {/* Eye Icon Button */}
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-4 top-[38px] text-slate-400 hover:text-emerald-700 transition-colors"
            >
              {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
            </button>
          </div>

          {/* Action Button */}
          <button
            type="submit"
            disabled={isLoading}
            className="w-full bg-emerald-800 hover:bg-emerald-700 active:bg-emerald-900 text-white font-medium tracking-wide py-3 px-4 rounded-xl shadow-lg shadow-emerald-800/20 transition-all duration-200 mt-2 flex items-center justify-center disabled:opacity-50"
          >
            {isLoading ? (
              <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
            ) : (
              "Sign In"
            )}
          </button>
        </form>

        {/* Footer Accent */}
        <div className="flex items-center justify-center mt-8 gap-2">
          <div className="w-8 h-[1px] bg-slate-200" />
          <div className="w-1.5 h-1.5 rotate-45 bg-amber-500" />
          <div className="w-8 h-[1px] bg-slate-200" />
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
